/* eslint-disable react/jsx-no-bind */
import { useCallback, useContext, useMemo, useState } from "react";

import { Box, Grid, Stack, useTheme } from "@mui/material";
import {
  CampaignPaymentTab,
  DashboardRoutes,
  FormType,
  OrganizationRoutes,
  RouteBuilders,
  TicketingFormCategory,
} from "@simplyk/common";
import { useSearchParams } from "next/navigation";
import { enqueueSnackbar } from "notistack";

import { FROM_ONBOARDING_QUERY_PARAM } from "../../../../common/constants/url";
import { AutoHideDuration } from "../../../../common/constants/Notistack";
import { FrontendFormContext } from "../../../../common/contexts/FrontendFormContext";
import { GetCurrentUserFrontendObject } from "../../../../common/contexts/types";
import { getDashboardLink } from "../../../../common/helpers/link-to-dashboard";
import { zeffyTheme } from "../../../../common/theme/theme";
import { ButtonCopy } from "../../ButtonCopy/ButtonCopy";

import { OwnerBannerLayout } from "./OwnerBannerLayout";
import { useShouldDisplayFormOwnerBanner } from "./useShouldDisplayFormOwnerBanner";

import { AmplitudeEvents } from "@/common/constants/amplitude";
import { useLocaleContext } from "@/common/contexts/LocaleContext";
import { useAmplitude } from "@/common/hooks/amplitude/useAmplitude";
import { useMediaQuery } from "@/common/hooks/useMediaQuery";
import { parseAsBoolean, useQueryState } from "@/common/hooks/useQueryState";
import { useTranslate } from "@/common/hooks/useTranslate";
import { useUrlBuilder } from "@/common/hooks/useUrlBuilder";
import { trpc } from "@/common/helpers/trpc";
import { ConnectedShareFormModal } from "@/components/ShareFormModal";
import { DashboardButton } from "@/dashboard-design-system/DashboardButton";
import { DashboardIcon } from "@/dashboard-design-system/Icon/DashboardIcon";
import { DashboardLink } from "@/dashboard-design-system/Link";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- shared auction end action; migrate when a cross-layer home exists
import { EndAuctionConfirmationDialog } from "@/features/Campaigns/components/CampaignCard/EndAuctionConfirmationDialog";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- shared auction end action; migrate when a cross-layer home exists
import { EndAuctionButton } from "@/features/Campaigns/components/EndAuctionButton";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { useNavigateToCampaignEditor } from "@/features/LiveFormEditor/Navigation/useNavigateToCampaignEditor";
import { Info } from "@/icons/outlined";

export const FormOwnerBanner = ({
  loggedOrganization,
}: {
  loggedOrganization: GetCurrentUserFrontendObject["currentOrganization"] | null | undefined;
}) => {
  const { isSmallScreen } = useMediaQuery();
  const { t } = useTranslate();
  const theme = useTheme();
  const { formData, formType } = useContext(FrontendFormContext);
  const { isoLocale } = useLocaleContext();
  const urlBuilder = useUrlBuilder();
  const [isShareModalOpen, setIsShareModalOpen] = useQueryState("share", parseAsBoolean);

  const buttonSize = isSmallScreen ? "small" : "medium";
  const buttonFullWidth = isSmallScreen;

  const formOrg = formData.organization;
  const isPaymentReady = formOrg?.isStripeCustomAccountActive;

  const campaignListUrl = urlBuilder({
    route: RouteBuilders.buildCampaignListLink({ isoLocale }),
  });
  const { getEditCampaignUrl } = useNavigateToCampaignEditor();

  const editFormLink = getEditCampaignUrl({
    formId: formData.id,
    formType,
    onExitUrl: campaignListUrl,
  });

  const shouldDisplayAdditionalStripeInfo = !isPaymentReady;
  const currentOrganizationId = loggedOrganization?.id;

  const [isEndAuctionModalOpen, setIsEndAuctionModalOpen] = useState(false);
  const { mutateAsync: endAuction } = trpc.endAuction.useMutation();
  const { logAmplitudeEvent } = useAmplitude();

  const isAuction =
    formType === FormType.Ticketing &&
    "formCategory" in formData &&
    formData.formCategory === TicketingFormCategory.Auction;

  const { data: auctionProcessingState } = trpc.form_getAuctionPaymentsProcessingState.useQuery(
    { ticketingId: formData.id },
    { enabled: isAuction && Boolean(currentOrganizationId) }
  );
  const canEndAuction = Boolean(auctionProcessingState?.hasBids) && !auctionProcessingState?.isClosed;

  const fulfillmentUrl = urlBuilder({
    route: RouteBuilders.buildCampaignHubTabLink({
      isoLocale,
      formId: formData.id,
      tab: CampaignPaymentTab.Fulfillment,
    }),
  });

  const handleEndAuction = useCallback(async () => {
    const { error } = await endAuction({ ticketingId: formData.id });
    if (error) {
      enqueueSnackbar(t("campaignHub", "endAuctionConfirmationDialog.snackbar.error"), {
        vibe: "danger",
      });
      return;
    }
    logAmplitudeEvent(AmplitudeEvents.AuctionEndedManually, {
      form_id: formData.id,
      entry_point: "admin_bar",
    });
    setIsEndAuctionModalOpen(false);
    window.location.assign(fulfillmentUrl);
  }, [endAuction, formData.id, logAmplitudeEvent, fulfillmentUrl, t]);

  const endAuctionButton = useMemo(
    () => <EndAuctionButton onClick={() => setIsEndAuctionModalOpen(true)} hideIcon={isSmallScreen} />,
    [isSmallScreen]
  );

  // When a non logged in user creates a P2P campaign, we store the formId and token in local storage
  // And we use it to display the edit and copy share link buttons
  const { isPeerToPeerCreator, isLoggedInCampaignCreator, token } = useShouldDisplayFormOwnerBanner({
    currentOrganizationId,
  });

  const searchParams = useSearchParams();
  const isFromOnboarding = searchParams.get(FROM_ONBOARDING_QUERY_PARAM) === "true";

  const onCopy = useCallback(() => {
    enqueueSnackbar(t("donationForm", "linkCopied"), {
      vibe: "positive",
      hideClose: true,
      autoHideDuration: AutoHideDuration.fast,
    });
  }, [t]);

  const sessionEditLink = urlBuilder({
    route: RouteBuilders.buildDonorDonationFormEditLink({ isoLocale, formId: formData.id }),
  });
  const editLink = isLoggedInCampaignCreator
    ? sessionEditLink
    : getDashboardLink(`${DashboardRoutes.FundraiserFormsEdit.replace(":id", formData.id)}&token=${token}`);
  const formLink = window.location.href;

  const editCampaignButton = useMemo(
    () => (
      <DashboardButton variant="outlined" vibe="neutral" href={editLink} size={buttonSize} fullWidth={buttonFullWidth}>
        {t("donationForm", "campaignIsLiveDialog.button.editCampaign")}
      </DashboardButton>
    ),
    [editLink, buttonSize, buttonFullWidth, t]
  );

  const copyCampaignLinkButton = useMemo(
    () => (
      <ButtonCopy
        variant="filled"
        vibe="brand"
        onClick={onCopy}
        toCopy={formLink}
        copyText={t("donationForm", "campaignIsLiveDialog.button.copyShareLink")}
        size={buttonSize}
        fullWidth={buttonFullWidth}
      />
    ),
    [buttonSize, buttonFullWidth, t, onCopy, formLink]
  );

  const buttons = useMemo(() => {
    if (isFromOnboarding && !isPeerToPeerCreator) {
      const shareCampaignButton = (
        <DashboardButton
          variant="outlined"
          onClick={() => setIsShareModalOpen(true)}
          size={buttonSize}
          fullWidth={buttonFullWidth}
        >
          {t("common", "shareForm")}
        </DashboardButton>
      );
      const continueToDashboardButton = (
        <DashboardButton variant="filled" href={campaignListUrl} size={buttonSize} fullWidth={buttonFullWidth}>
          {t("common", "continueToDashboard")}
        </DashboardButton>
      );

      return isSmallScreen ? (
        <Grid container columnSpacing={2} sx={{ marginTop: zeffyTheme.spacing(1.5) }}>
          <Grid item xs={6}>
            {shareCampaignButton}
          </Grid>
          <Grid item xs={6}>
            {continueToDashboardButton}
          </Grid>
        </Grid>
      ) : (
        <Stack direction="row" spacing={2}>
          {shareCampaignButton}
          {continueToDashboardButton}
        </Stack>
      );
    }

    if (isSmallScreen) {
      if (isPaymentReady) {
        return (
          <Grid container columnSpacing={2} rowSpacing={1.5} sx={{ marginTop: zeffyTheme.spacing(1.5) }}>
            {canEndAuction && (
              <Grid item xs={12}>
                {endAuctionButton}
              </Grid>
            )}
            <Grid item xs={6}>
              {isPeerToPeerCreator ? (
                editCampaignButton
              ) : (
                <DashboardButton
                  variant="outlined"
                  href={campaignListUrl}
                  size={buttonSize}
                  fullWidth={buttonFullWidth}
                >
                  {t("common", "backToDashboard")}
                </DashboardButton>
              )}
            </Grid>
            <Grid item xs={6}>
              {isPeerToPeerCreator ? (
                copyCampaignLinkButton
              ) : (
                <DashboardButton
                  variant="filled"
                  onClick={() => setIsShareModalOpen(true)}
                  size={buttonSize}
                  fullWidth={buttonFullWidth}
                >
                  {t("common", "shareForm")}
                </DashboardButton>
              )}
            </Grid>
          </Grid>
        );
      } else {
        return (
          <Grid container columnSpacing={2} sx={{ marginTop: zeffyTheme.spacing(1.5) }}>
            <Grid item xs={6}>
              <DashboardButton variant="outlined" href={editFormLink} size={buttonSize} fullWidth={buttonFullWidth}>
                {t("common", "editForm")}
              </DashboardButton>
            </Grid>
            <Grid item xs={6}>
              <DashboardButton variant="filled" href={campaignListUrl} size={buttonSize} fullWidth={buttonFullWidth}>
                {t("common", "backToDashboard")}
              </DashboardButton>
            </Grid>
          </Grid>
        );
      }
    } else {
      if (isPaymentReady) {
        return (
          <>
            <Stack direction="row" spacing={2}>
              {canEndAuction && endAuctionButton}
              {isPeerToPeerCreator ? (
                editCampaignButton
              ) : (
                <DashboardButton
                  variant="outlined"
                  href={campaignListUrl}
                  size={buttonSize}
                  fullWidth={buttonFullWidth}
                >
                  {t("common", "backToDashboard")}
                </DashboardButton>
              )}
              {isPeerToPeerCreator ? (
                copyCampaignLinkButton
              ) : (
                <DashboardButton
                  variant="filled"
                  onClick={() => setIsShareModalOpen(true)}
                  size={buttonSize}
                  fullWidth={buttonFullWidth}
                >
                  {t("common", "shareForm")}
                </DashboardButton>
              )}
            </Stack>
          </>
        );
      } else {
        return (
          <Stack direction="row" spacing={2}>
            <DashboardButton variant="outlined" href={editFormLink} size={buttonSize} fullWidth={buttonFullWidth}>
              {t("common", "editForm")}
            </DashboardButton>
            <DashboardButton variant="filled" href={campaignListUrl} size={buttonSize} fullWidth={buttonFullWidth}>
              {t("common", "backToDashboard")}
            </DashboardButton>
          </Stack>
        );
      }
    }
  }, [
    isFromOnboarding,
    isSmallScreen,
    isPaymentReady,
    isPeerToPeerCreator,
    editCampaignButton,
    campaignListUrl,
    buttonSize,
    buttonFullWidth,
    t,
    copyCampaignLinkButton,
    setIsShareModalOpen,
    editFormLink,
    canEndAuction,
    endAuctionButton,
  ]);

  const callout = shouldDisplayAdditionalStripeInfo ? (
    <Stack direction="row" spacing={1}>
      <Box
        sx={{
          backgroundColor: theme.palette.surface.brand.quiet,
          paddingBlock: theme.spacing(1.5),
          paddingInline: theme.spacing(2),
          borderRadius: theme.radius(1),
          width: isSmallScreen ? "100%" : "auto",
        }}
      >
        <Stack direction="row" gap={1.5} alignItems="center">
          <Box sx={{ lineHeight: 0 }}>
            {!isSmallScreen && (
              <DashboardIcon vibe="text-brand-moderate" size="medium">
                <Info />
              </DashboardIcon>
            )}
          </Box>
          <Box>
            <DashboardTypography variant="body2" align="left" vibe="text-brand-moderate" component="span">
              {t("common", "linkBankAccountPrompt1")}{" "}
              <DashboardLink href={OrganizationRoutes.Bank} sx={{ textDecoration: "underline" }}>
                {t("common", "linkBankAccountPrompt2")}
              </DashboardLink>
              .
            </DashboardTypography>
          </Box>
        </Stack>
      </Box>
    </Stack>
  ) : null;

  return (
    <>
      <ConnectedShareFormModal
        formId={formData.id}
        formType={formType}
        open={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        source="form_donator_view"
        hideViewFormButton
        sx={{ zIndex: 10000 }}
      />
      <EndAuctionConfirmationDialog
        open={isEndAuctionModalOpen}
        onClose={() => setIsEndAuctionModalOpen(false)}
        onConfirm={handleEndAuction}
      />
      <OwnerBannerLayout
        slots={{
          callout,
          actions: buttons,
        }}
      />
    </>
  );
};
